import React, { useState, useMemo } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';

import { filterData, formatValue } from '@ts-core/utils/dataTableUtils';

import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';

import AnswerResultIcon from '@ts-core/core/components/AnswerResultIcon';
import TSTable from '@ts-core/components/Table/TSTable';
import { TSTableColumn } from '@ts-core/components/Table/TSTable/TSTable';

import { RankingStudent, RankingItem } from '../../types';

interface SubjectsRankingTableProps {
  downloadFileName?: string;
  datesPicker?: React.ReactNode;
  studentsMode?: boolean;
  students?: RankingStudent[];
  subjects?: RankingItem[];
  error?: string;
  loading?: boolean;
  getRankingBySubjects?: () => void;
}

const SubjectsRankingTable = (props: SubjectsRankingTableProps) => {
  const intl = useIntl();

  const {
    downloadFileName,
    datesPicker,
    students = [],
    subjects = [],
    studentsMode,
    getRankingBySubjects,
    loading = false,
    error: errorMessage,
  } = props;

  const [query, setQuery] = useState('');
  const [showOnlyNoAnswerStudents, setShowOnlyNoAnswerStudents] = useState(false);
  const [selectedSubject, setSelectedSubject] = useState('all');

  const extraFilters = useMemo(() => {
    return [
      {
        component: (
          <FormControlLabel
            key="students"
            control={<Checkbox checked={showOnlyNoAnswerStudents} onChange={(event) => setShowOnlyNoAnswerStudents(event.target.checked)} />}
            label={<FormattedMessage id="assignmentResultsTable.filter.onlyStudentsWithoutAnswers" />}
          />
        ),
      },
    ];
  }, [showOnlyNoAnswerStudents]);

  const enabledSubjects = useMemo(() => {
    return selectedSubject === 'all' ? subjects : subjects.filter((s) => s.id === selectedSubject);
  }, [selectedSubject, subjects]);

  const columns = useMemo((): TSTableColumn<RankingStudent>[] => {
    const result: TSTableColumn<RankingStudent>[] = [
      {
        Header: intl.formatMessage({ id: 'assignmentResultsTable.student' }),
        value: intl.formatMessage({ id: 'assignmentResultsTable.student' }),
        accessor: 'name',
        sortable: true,
        Footer: intl.formatMessage({ id: 'averages' }),
      },
    ];

    if (students.some((s) => s.group)) {
      result.push({
        Header: intl.formatMessage({ id: 'studentsTable.column.group' }),
        value: intl.formatMessage({ id: 'studentsTable.column.group' }),
        accessor: 'group',
        id: 'group',
        sortable: true,
        filterable: true,
      });
    }

    enabledSubjects.forEach((subject) => {
      const { stats } = subject;
      result.push({
        Header: subject.name,
        value: subject.name,
        accessor: `scores.${subject.id}`,
        valueTransformer: (value) => (value !== undefined ? formatValue(value) : <AnswerResultIcon type="none" />),
        sortable: true,
        Footer: formatValue(stats.answerTypes?.positive, stats.maxScore),
      });
    });

    return result;
  }, [enabledSubjects, intl, students]);

  const keys = ['name', 'group'];

  let filteredData = filterData(students, query, keys);
  if (showOnlyNoAnswerStudents) {
    filteredData = filteredData.filter((s: RankingStudent) => {
      return !enabledSubjects.some((subject) => s.scores && s.scores[subject.id] !== undefined);
    });
  }

  const title = (
    <Box display="flex" flexDirection="column">
      {datesPicker}
      <Box mt={2} ml={-0.5}>
        <FormControl fullWidth size="small">
          <InputLabel>
            <FormattedMessage id="subjects" />
          </InputLabel>
          <Select
            label={<FormattedMessage id="subjects" />}
            value={selectedSubject || 'all'}
            fullWidth
            onChange={(event) => setSelectedSubject(event.target.value)}
          >
            <MenuItem value="all">
              <FormattedMessage id="ranking.subjectsFilter.all" />
            </MenuItem>
            {subjects.map((subject, index) => (
              <MenuItem key={index} value={subject.id}>
                {subject.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    </Box>
  );

  return (
    <TSTable
      title={!studentsMode && title}
      data={filteredData}
      columns={columns}
      onSearch={(text) => setQuery(text)}
      allowDownloadAsXLSX={!studentsMode}
      loading={loading}
      errorMessage={errorMessage}
      xlsxFileName={downloadFileName}
      extraFilters={studentsMode ? [] : extraFilters}
      onRefreshData={getRankingBySubjects}
    />
  );
};

export default SubjectsRankingTable;
